import type { CopilotStatus } from '@brain/contracts'
import { Button, Card, Chip, cn, EmptyState, Kbd, Textarea, toast } from '@brain/ui'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import type { TFunction } from 'i18next'
import { Monitor, MonitorSpeaker, Play, Radio, Sparkles, Square, X } from 'lucide-react'
import { Switch } from 'radix-ui'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { LevelMeter } from '../../components/live/LevelMeter.tsx'
import { LiveTranscript } from '../../components/live/LiveTranscript.tsx'
import { ResizeHandle, useResizableWidth } from '../../components/live/ResizeHandle.tsx'
import { TipCard } from '../../components/live/TipCard.tsx'
import { MeetingNotesEditor } from '../../components/meetings/MeetingNotesEditor.tsx'
import { liveSessionQuery } from '../../lib/live/api.ts'
import { joinGerman, knowledgeParts } from '../../lib/live/blocks.ts'
import type { ConnState } from '../../lib/live/socket.ts'
import { useLiveMeeting } from '../../lib/live/use-live-meeting.ts'
import { meetingQuery } from '../../lib/m2.ts'
import { formatMs } from '../../lib/transcript.ts'

export const Route = createFileRoute('/_app/meetings/live/$meetingId')({ component: LiveMeetingPage })

const PANEL_KEY = 'brain.live.copilotWidth'
/** image blocks in the notes Markdown – the co-pilot gets the text only */
const IMAGE_MD = /!\[[^\]]*\]\([^)]*\)/g
const PANEL_DEFAULT = 420

function useElapsed(startedAt: string | null): number {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(id)
  }, [])
  return startedAt ? now - new Date(startedAt).getTime() : 0
}

const connTone = (s: ConnState | undefined) =>
  s === 'open' ? 'bg-green' : s === 'error' || s === 'closed' ? 'bg-apricot' : 'bg-sky'

function LiveMeetingPage() {
  const { t } = useTranslation()
  const { meetingId } = Route.useParams()
  const navigate = useNavigate()
  const { me } = Route.useRouteContext()
  const live = useLiveMeeting(
    meetingId,
    (id) => void navigate({ to: '/meetings/$meetingId', params: { meetingId: id }, replace: true }),
  )
  const created = useQuery(liveSessionQuery(meetingId)).data
  const { state } = live
  const elapsed = useElapsed(state.startedAt)
  const [confirmEnd, setConfirmEnd] = useState(false)
  const [mobileTab, setMobileTab] = useState<'transcript' | 'notes' | 'copilot'>('transcript')
  const [panelTab, setPanelTab] = useState<'copilot' | 'notes'>('copilot')
  const meeting = useQuery(meetingQuery(meetingId)).data
  const rowRef = useRef<HTMLDivElement>(null)
  const panel = useResizableWidth(PANEL_KEY, PANEL_DEFAULT, rowRef)
  const unavailableReason =
    state.status.state === 'off'
      ? state.status.reason
      : created && !created.copilot
        ? (created.copilotReason ?? '')
        : null
  const showPanel = unavailableReason === null && state.copilotEnabled
  useEffect(() => {
    if (!showPanel) setMobileTab((x) => (x === 'copilot' ? 'transcript' : x))
  }, [showPanel])
  const mobileTabs = showPanel
    ? (['transcript', 'copilot', 'notes'] as const)
    : (['transcript', 'notes'] as const)
  // right column: notes only while the co-pilot is off; on mobile the bottom tab decides
  const rightTab: 'copilot' | 'notes' = !showPanel
    ? 'notes'
    : mobileTab === 'copilot' || mobileTab === 'notes'
      ? mobileTab
      : panelTab
  const setRightTab = (x: 'copilot' | 'notes') => setPanelTab(x)
  // a correction fixed more places than the edited one: say so once
  const correctionSeq = state.lastCorrection?.seq
  // biome-ignore lint/correctness/useExhaustiveDependencies: announce each correction once (seq)
  useEffect(() => {
    const c = state.lastCorrection
    if (c) toast(t('live.corrected', { from: c.from, to: c.to, count: c.count }))
  }, [correctionSeq])

  if (live.gone)
    return (
      <div className="flex flex-1 items-center justify-center p-6">
        <EmptyState
          icon={<Radio size={20} strokeWidth={1.9} />}
          title={t('live.notFound')}
          description={live.gone}
          action={
            <Link to="/meetings/$meetingId" params={{ meetingId }}>
              <Button size={32} variant="secondary" tabIndex={-1}>
                {t('nav.meetings')}
              </Button>
            </Link>
          }
        />
      </div>
    )

  const title = state.title
  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col">
      <header
        data-tauri-drag-region
        className="flex flex-wrap items-center gap-x-4 gap-y-2 border-line-1 border-b px-4 py-3 md:px-6"
      >
        <div className="flex min-w-0 flex-1 items-center gap-2">
          <span className="relative flex size-2.5 shrink-0" aria-hidden>
            <span
              className={cn(
                'absolute inline-flex size-full rounded-pill opacity-60',
                connTone(live.conn),
                live.conn === 'open' && 'animate-ping',
              )}
            />
            <span className={cn('relative inline-flex size-2.5 rounded-pill', connTone(live.conn))} />
          </span>
          <h1 className="truncate text-[17px] text-text-1">{title || t('live.dialogTitle')}</h1>
          <span className="font-mono text-[13px] text-meta">{formatMs(elapsed)}</span>
          {live.conn !== 'open' && (
            <span className="text-[12px] text-meta">{t(`live.socket.${live.conn}`)}</span>
          )}
        </div>
        <div className="flex items-center gap-3">
          <LevelMeter label={t('live.levelMe')} level={live.levels.mic} off={live.mic !== 'on'} />
          <LevelMeter label={t('live.levelMeeting')} level={live.levels.system} off={live.system !== 'on'} />
          {(['mic', 'system'] as const).map((ch) => {
            const c = state.channels[ch]
            return c && c.state !== 'open' ? (
              <span key={ch} className="text-[11px] text-meta" title={c.message}>
                {ch === 'mic' ? t('live.levelMe') : t('live.levelMeeting')}: {t(`live.conn.${c.state}`)}
              </span>
            ) : null
          })}
        </div>
        <div className="flex items-center gap-2">
          {live.mic === 'suspended' && (
            <Button
              size={32}
              variant="secondary"
              onClick={() => void live.resumeAudio()}
              title={t('live.micSuspended')}
            >
              <Play size={14} />
              {t('live.resumeAudio')}
            </Button>
          )}
          {live.systemMode !== 'none' && live.system !== 'on' && live.system !== 'starting' && (
            <Button
              size={32}
              variant="secondary"
              onClick={() => void live.shareSystem()}
              title={live.systemMode === 'display' ? t('live.shareSystemHint') : undefined}
            >
              <MonitorSpeaker size={14} />
              {live.systemMode === 'display' ? t('live.shareSystem') : t('live.retrySystem')}
            </Button>
          )}
          {live.systemMode === 'none' && <Chip title={t('live.sysNone')}>{t('live.micOnly')}</Chip>}
          {state.recording && (
            <span
              className="flex items-center gap-1.5 rounded-pill bg-apricot-bg px-2.5 py-1 font-medium text-[12px] text-apricot-fg"
              title={
                state.recording.video
                  ? t('live.recordingVideo', { count: live.frameCount })
                  : t('live.recordingAudio')
              }
            >
              <span className="size-2 animate-pulse rounded-pill bg-apricot" aria-hidden />
              {t('live.recording')}
            </span>
          )}
          {live.frames === 'needed' && !(live.systemMode === 'display' && live.system !== 'on') && (
            <Button size={32} variant="secondary" onClick={() => void live.shareScreen()}>
              <Monitor size={14} />
              {t('live.shareScreen')}
            </Button>
          )}
          <CopilotSwitch
            enabled={showPanel}
            unavailableReason={unavailableReason}
            disabled={live.conn !== 'open'}
            onChange={live.setCopilot}
          />
          {confirmEnd ? (
            <>
              <Button size={32} variant="ghost" onClick={() => setConfirmEnd(false)}>
                {t('common.cancel')}
              </Button>
              <Button size={32} variant="danger" disabled={live.ending} onClick={() => void live.end()}>
                {live.ending ? t('live.ending') : t('live.end')}
              </Button>
            </>
          ) : (
            <Button size={32} variant="danger" onClick={() => setConfirmEnd(true)}>
              <Square size={13} />
              {t('live.end')}
            </Button>
          )}
        </div>
        {confirmEnd && !live.ending && (
          <p className="w-full text-right text-[12px] text-text-2">{t('live.endConfirm')}</p>
        )}
      </header>

      <div className="flex border-line-1 border-b md:hidden" role="tablist">
        {mobileTabs.map((x) => (
          <button
            key={x}
            type="button"
            role="tab"
            aria-selected={mobileTab === x}
            onClick={() => setMobileTab(x)}
            className={cn(
              'h-10 flex-1 border-b-2 font-medium text-[13px]',
              mobileTab === x ? 'border-pop text-text-1' : 'border-transparent text-meta',
            )}
          >
            {x === 'transcript'
              ? t('live.transcript')
              : x === 'notes'
                ? t('live.notes.tab')
                : t('live.copilotTab')}
            {x === 'copilot' && state.tips.length > 0 && ` (${state.tips.length})`}
          </button>
        ))}
      </div>

      <div ref={rowRef} className="flex min-h-0 flex-1">
        <section
          className={cn(
            'min-h-0 min-w-0 flex-1 flex-col',
            mobileTab === 'transcript' ? 'flex' : 'hidden md:flex',
          )}
          aria-label={t('live.transcript')}
        >
          <div className="flex items-center justify-end gap-2 border-line-1 border-b px-4 py-1.5 md:px-6">
            <span className="text-[12px] text-meta">{t('live.remoteSpeakers.label')}</span>
            {(['single', 'auto'] as const).map((mode) => (
              <button
                key={mode}
                type="button"
                disabled={live.conn !== 'open'}
                aria-pressed={state.remoteSpeakers === mode}
                title={t(`live.remoteSpeakers.${mode}Hint`)}
                onClick={() => live.setRemoteSpeakers(mode)}
                className={cn(
                  'h-7 rounded-pill px-2.5 text-[12px]',
                  state.remoteSpeakers === mode
                    ? 'bg-pop-tint font-medium text-pop-fg'
                    : 'text-text-2 hover:bg-grey-1',
                )}
              >
                {t(`live.remoteSpeakers.${mode}`)}
              </button>
            ))}
          </div>
          <LiveTranscript
            segments={state.segments}
            interim={state.interim}
            names={state.speakerNames}
            containerId={meeting?.containerId ?? null}
            onRename={live.conn === 'open' ? live.setSpeaker : undefined}
            onEdit={live.conn === 'open' ? live.editSegment : undefined}
          />
        </section>
        <ResizeHandle containerRef={rowRef} onResize={panel.setWidth} onReset={panel.reset} />
        <section
          className={cn(
            'min-h-0 w-full flex-col border-line-1 bg-page md:w-[var(--panel-w)] md:shrink-0 md:border-l',
            mobileTab === 'transcript' ? 'hidden md:flex' : 'flex',
          )}
          style={{ '--panel-w': `${panel.width}px` } as React.CSSProperties}
          aria-label={rightTab === 'copilot' ? t('live.copilotTab') : t('live.notes.tab')}
        >
          {showPanel && (
            <div className="hidden border-line-1 border-b md:flex" role="tablist">
              {(['copilot', 'notes'] as const).map((x) => (
                <button
                  key={x}
                  type="button"
                  role="tab"
                  aria-selected={rightTab === x}
                  onClick={() => setRightTab(x)}
                  className={cn(
                    'h-10 flex-1 border-b-2 font-medium text-[13px]',
                    rightTab === x ? 'border-pop text-text-1' : 'border-transparent text-meta',
                  )}
                >
                  {x === 'notes' ? t('live.notes.tab') : t('live.copilotTab')}
                </button>
              ))}
            </div>
          )}
          {/* both stay mounted so the notes draft survives tab switches */}
          <div className={cn('min-h-0 flex-1 flex-col', rightTab === 'copilot' ? 'flex' : 'hidden')}>
            <CopilotPanel live={live} knowledge={created?.knowledge} />
          </div>
          <div className={cn('min-h-0 flex-1 flex-col p-4', rightTab === 'notes' ? 'flex' : 'hidden')}>
            {/* ADR 0015: same editor as notes; its Markdown is mirrored to the co-pilot (without image links) */}
            {meeting ? (
              <MeetingNotesEditor
                meeting={meeting}
                user={{ id: me.id, name: me.name }}
                autoCreate
                live
                onMarkdown={(md) => live.setNotes(md.replace(IMAGE_MD, '').trim())}
                className="flex-1"
              />
            ) : (
              <p className="text-[13px] text-meta">{t('common.loading')}</p>
            )}
          </div>
        </section>
      </div>
    </div>
  )
}

function statusText(t: TFunction, s: CopilotStatus): string {
  switch (s.state) {
    case 'listening':
      return t('live.status.listening')
    case 'thinking':
      return s.trigger === 'manual' ? t('live.status.thinkingManual') : t('live.status.thinkingAuto')
    case 'skipped':
      return t('live.status.skipped', { time: formatMs(s.atMs) })
    case 'off':
      return t('live.status.off', { reason: s.reason })
    case 'paused':
      return t('live.status.paused')
  }
}

function CopilotSwitch({
  enabled,
  unavailableReason,
  disabled,
  onChange,
}: {
  enabled: boolean
  unavailableReason: string | null
  disabled: boolean
  onChange: (enabled: boolean) => void
}) {
  const { t } = useTranslation()
  const unavailable = unavailableReason !== null
  const hint = unavailable
    ? t('live.copilotUnavailable', { reason: unavailableReason })
    : t(enabled ? 'live.copilotOff' : 'live.copilotOn')
  return (
    <div
      className={cn('flex items-center gap-2 text-[13px]', unavailable ? 'text-meta' : 'text-text-1')}
      title={hint}
    >
      <Sparkles size={14} className={enabled ? 'text-pop-fg' : 'text-meta'} />
      <span id="copilot-switch-label" className="font-medium">
        {t('live.copilotSwitch')}
      </span>
      <Switch.Root
        checked={enabled}
        disabled={unavailable || disabled}
        onCheckedChange={onChange}
        aria-label={hint}
        className={cn(
          'relative h-5 w-9 shrink-0 rounded-pill bg-line-2 transition-colors data-[state=checked]:bg-pop',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pop/25 disabled:opacity-50',
        )}
      >
        <Switch.Thumb className="block size-4 translate-x-0.5 rounded-pill bg-card shadow-1 transition-transform data-[state=checked]:translate-x-[18px]" />
      </Switch.Root>
    </div>
  )
}

function CopilotPanel({
  live,
  knowledge,
}: {
  live: ReturnType<typeof useLiveMeeting>
  knowledge: Parameters<typeof knowledgeParts>[0] | undefined
}) {
  const { t } = useTranslation()
  const [question, setQuestion] = useState('')
  const { state } = live
  const off = state.status.state === 'off' || state.status.state === 'paused'
  const parts = knowledge ? knowledgeParts(knowledge) : []
  const send = () => {
    live.ask(question)
    setQuestion('')
  }
  return (
    <>
      <div className="flex flex-col gap-1 border-line-1 border-b px-4 py-3">
        <p className="flex items-center gap-2 text-[13px] text-text-2">
          <Sparkles
            size={14}
            className={cn(state.status.state === 'thinking' ? 'animate-pulse text-pop-fg' : 'text-meta')}
          />
          <span className="min-w-0 truncate">{statusText(t, state.status)}</span>
        </p>
        {knowledge && (
          <p className="text-[12px] text-meta">
            {parts.length
              ? t('live.knowledge', {
                  summary: joinGerman(
                    parts.map((p) => t(`live.knowledgeParts.${p.key}`, { count: p.count })),
                  ),
                })
              : t('live.knowledgeNone')}
          </p>
        )}
      </div>

      <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto px-4 py-4">
        {state.errors.map((e) => (
          <Card
            key={e.id}
            role="alert"
            className="flex items-start gap-2 border-apricot bg-apricot-bg px-3 py-2 text-[12px] text-apricot-fg"
          >
            <span className="min-w-0 flex-1">{e.message}</span>
            <button
              type="button"
              aria-label={t('live.dismiss')}
              onClick={() => live.dispatch({ type: 'dismiss-error', id: e.id })}
              className="shrink-0 hover:opacity-70"
            >
              <X size={13} />
            </button>
          </Card>
        ))}
        {!state.tips.length && !off && (
          <p className="m-auto max-w-xs text-center text-[13px] text-meta">{t('live.tipsEmpty')}</p>
        )}
        {state.tips.map((tip, i) => (
          <TipCard key={tip.id} tip={tip} featured={i === 0} />
        ))}
      </div>

      <form
        className="flex flex-col gap-2 border-line-1 border-t px-4 py-3"
        onSubmit={(e) => {
          e.preventDefault()
          send()
        }}
      >
        <Textarea
          rows={2}
          value={question}
          disabled={off}
          onChange={(e) => setQuestion(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
              e.preventDefault()
              send()
            }
          }}
          placeholder={t('live.askPlaceholder')}
          maxLength={2000}
          className="resize-none text-[13px]"
        />
        <div className="flex items-center justify-end gap-2">
          <span className="flex items-center gap-1 text-[11px] text-meta">
            <Kbd>⌘</Kbd>
            <Kbd>↵</Kbd>
          </span>
          <Button type="submit" size={32} disabled={off || live.conn !== 'open'}>
            <Sparkles size={14} />
            {t('live.help')}
          </Button>
        </div>
      </form>
    </>
  )
}
